// Lightweight IndexedDB-backed key/value store.
//
// Replaces localStorage for large/binary-ish payloads (data URLs, generated
// media metadata, etc.) that would otherwise blow past localStorage quotas.
//
// Values are stored via IndexedDB's structured clone, so objects and arrays are
// persisted and returned as-is (no JSON.stringify/parse needed by callers).
//
// idbSet is debounced/coalesced per key so rapid successive writes (e.g. on
// every keystroke) only hit the DB once. Call flushAllPending() to force any
// outstanding writes to disk immediately (e.g. on page hide/unload).

const DB_NAME = "music-gen-store";
const STORE_NAME = "kv";
const DB_VERSION = 1;

let dbPromise = null;

function openDB() {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") {
      reject(new Error("IndexedDB is not available in this environment"));
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return dbPromise;
}

function withStore(mode, fn) {
  return openDB().then(
    (db) =>
      new Promise((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, mode);
        const store = tx.objectStore(STORE_NAME);
        let result;
        const maybe = fn(store, (r) => {
          result = r;
        });
        tx.oncomplete = () => resolve(result !== undefined ? result : maybe);
        tx.onerror = () => reject(tx.error);
        tx.onabort = () => reject(tx.error);
      })
  );
}

export function idbGet(key) {
  return withStore("readonly", (store, setResult) => {
    const req = store.get(key);
    req.onsuccess = () => setResult(req.result === undefined ? null : req.result);
  });
}

export function idbSetImmediate(key, value) {
  return withStore("readwrite", (store) => {
    store.put(value, key);
  });
}

export function idbRemove(key) {
  // Cancel any pending debounced write for this key so it can't resurrect the
  // value after removal.
  const pending = pendingWrites.get(key);
  if (pending) {
    clearTimeout(pending.timer);
    pendingWrites.delete(key);
    pending.resolve();
  }
  return withStore("readwrite", (store) => {
    store.delete(key);
  });
}

export function idbRemoveByPrefix(prefix) {
  // Drop any pending debounced writes that match the prefix first.
  for (const [key, pending] of pendingWrites) {
    if (key.startsWith(prefix)) {
      clearTimeout(pending.timer);
      pendingWrites.delete(key);
      pending.resolve();
    }
  }
  return withStore("readwrite", (store) => {
    const req = store.openCursor();
    req.onsuccess = () => {
      const cursor = req.result;
      if (!cursor) return;
      if (typeof cursor.key === "string" && cursor.key.startsWith(prefix)) {
        cursor.delete();
      }
      cursor.continue();
    };
  });
}

// --- Debounced writes -------------------------------------------------------

const WRITE_DELAY_MS = 400;
// key -> { timer, value, resolve, reject }
const pendingWrites = new Map();

export function idbSet(key, value) {
  return new Promise((resolve, reject) => {
    const existing = pendingWrites.get(key);
    if (existing) {
      clearTimeout(existing.timer);
      // Resolve the superseded promise; its write is replaced by this newer one.
      existing.resolve();
    }
    const entry = { value, resolve, reject, timer: null };
    entry.timer = setTimeout(() => {
      pendingWrites.delete(key);
      idbSetImmediate(key, entry.value).then(resolve, reject);
    }, WRITE_DELAY_MS);
    pendingWrites.set(key, entry);
  });
}

export function flushAllPending() {
  const entries = Array.from(pendingWrites.entries());
  pendingWrites.clear();
  const promises = entries.map(([key, entry]) => {
    clearTimeout(entry.timer);
    return idbSetImmediate(key, entry.value).then(entry.resolve, entry.reject);
  });
  return Promise.all(promises);
}
